import { useEffect, useMemo, useState } from 'react';

import { AnswerContent } from './answer-content';
import { AnswerHeader } from './answer-header';
import { AnswerSidebar } from './answer-sidebar';
import { FollowUpQuestions } from './follow-up-questions';
import { NoAnswer } from './no-answer';
import { SupportingChunks } from './supporting-chunks';
import { SupportingFacts } from './supporting-facts';
import { getConfidenceLevel } from '@/helpers/kimDisplay';
import { KimMode, MessageType, ThreadMessage } from '@/types';

interface AnswerProps {
  threadId: string;
  caseId: string;
  messages: ThreadMessage[] | undefined;
  isLoadingMessages: boolean;
}

export function Answer({ threadId, caseId, isLoadingMessages, messages }: AnswerProps) {
  const [showAllChunks, setShowAllChunks] = useState(false);

  // If there are no facts but there are chunks, show the chunks
  useEffect(() => {
    if (!messages) return;
    const answerMessage = messages?.find((message) => message.type === MessageType.Assistant);
    if (!answerMessage) return;
    const { facts, chunks } = answerMessage;
    if (!facts || !chunks) return;
    if (facts.length === 0 && chunks.length > 0) {
      setShowAllChunks(true);
    }
  }, [messages]);

  const userMessage = messages?.find((message) => message.type === MessageType.User);
  const answerMessage = messages?.find((message) => message.type === MessageType.Assistant);
  const confidenceLevel = getConfidenceLevel(answerMessage?.config?.trafficLight);
  const content = answerMessage?.content || '';
  const reasoning = answerMessage?.reasoning || '';
  const facts = answerMessage?.facts || [];
  const chunks = answerMessage?.chunks || [];
  const flowType = answerMessage?.config?.flowType;
  const artifact = answerMessage?.config?.artifacts?.[0];
  const references = answerMessage?.references || {};
  const followUpQuestions = answerMessage?.config?.followUpQuestions || [];
  const messageId = answerMessage?.id || '';

  const referencedDocIds = useMemo(
    () => [...facts.flatMap((f) => f.locations.map((l) => l.docId)), ...chunks.map((c) => c.docId)],
    [facts, chunks],
  );

  return (
    <div className="flex h-full">
      <AnswerSidebar userMessage={userMessage} isLoadingMessages={isLoadingMessages} />

      {!isLoadingMessages && (
        <div className="grow overflow-x-hidden">
          <AnswerHeader
            confidenceLevel={confidenceLevel}
            content={content}
            caseId={caseId}
            threadId={threadId}
            messageId={messageId}
            artifact={artifact}
            flowType={flowType}
            isLoadingMessages={isLoadingMessages}
          />

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {content && content.length > 0 ? (
              <>
                <AnswerContent content={content} reasoning={reasoning} references={references} flowType={flowType} />
                {flowType !== KimMode.ExtractionFlow && flowType !== undefined && (
                  <>
                    <SupportingFacts
                      facts={facts}
                      confidenceLevel={confidenceLevel}
                      references={references}
                      docIds={referencedDocIds}
                    />
                    <SupportingChunks
                      setShowAllChunks={setShowAllChunks}
                      showAllChunks={showAllChunks}
                      chunks={chunks}
                    />
                    <FollowUpQuestions followUpQuestions={followUpQuestions} questionMode={flowType} />
                  </>
                )}
              </>
            ) : (
              <NoAnswer />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
